import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaFileExcel,
  FaPrint,
  FaFilter,
  FaTimes,
  FaBox,
} from "react-icons/fa";

import {
  CalendarClock,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const FinYearPendingTransaction = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(ThemeContext);

  const currentSkin =
    getCurrentSkin(skin);

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const storedBranchId =
    localStorage.getItem(
      "branchId"
    ) || "8";

  const storedBranchType =
    localStorage.getItem(
      "branchType"
    ) || "HO";

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          storedBranchId,

        "x-branch":
          storedBranchType,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        storedBranchId,
        storedBranchType,
        userId,
      ]
    );

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    branchType,
    setBranchType,
  ] = useState(
    storedBranchType || "HO"
  );

  const [
    branchId,
    setBranchId,
  ] = useState(
    storedBranchId || "8"
  );

  const [
    projectCode,
    setProjectCode,
  ] = useState("");

  const [
    supplier,
    setSupplier,
  ] = useState("");

  /* =========================================================
     DROPDOWN DATA
  ========================================================= */

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  const [
    supplierList,
    setSupplierList,
  ] = useState([]);

  /* =========================================================
     RESULT DATA
  ========================================================= */

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     TEMPLATE DESIGN CLASSES
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const disabledInputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    cursor-not-allowed
    ${
      isDark
        ? "bg-slate-800/40 border-slate-700 text-slate-400"
        : "bg-slate-100 border-slate-300 text-slate-500"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     LOAD DROPDOWNS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/dropdown_finRpt",
        {
          params: {
            branchid:
              branchId ||
              storedBranchId ||
              "8",

            branchtype:
              branchType ||
              storedBranchType ||
              "HO",
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const stores =
          res.data.branches?.list ||
          res.data.branches ||
          res.data.data?.branches ||
          [];

        const suppliers =
          res.data.suppliers ||
          res.data.data?.suppliers ||
          [];

        setBranchList(
          stores
        );

        setSupplierList(
          suppliers
        );

        if (
          !branchId &&
          stores.length > 0
        ) {
          setBranchId(
            String(
              stores[0].id ||
                stores[0].fld_Branchid ||
                stores[0].value ||
                storedBranchId ||
                "8"
            )
          );
        }
      })
      .catch((err) => {
        console.error(
          "Dropdown fetch failed",
          err
        );

        toast.error(
          "Failed to load dropdowns"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    branchType,
    branchId,
    storedBranchId,
    storedBranchType,
    commonHeaders,
  ]);

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch = () => {
    setLoading(true);

    const payload = {
      finyrname:
        finYear ||
        "2022-2023",

      branch:
        branchId ||
        storedBranchId ||
        "8",

      supplier:
        supplier ||
        "%",

      type:
        projectCode ||
        "%",
    };

    axios
      .get(
        "https://nissi-india.com/api/finPendingTrans_listRpt",
        {
          params:
            payload,

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const rows =
          res.data.records ||
          res.data.data ||
          res.data.rows ||
          res.data.recordset ||
          res.data
            .recordsets?.[0] ||
          [];

        setData(
          rows
        );

        toast.success(
          `Loaded ${rows.length} records`
        );
      })
      .catch((err) => {
        console.error(
          "Pending transaction fetch failed",
          err
        );

        toast.error(
          "Failed to fetch pending transactions"
        );

        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* =========================================================
     EXPORT EXCEL
     EXISTING REAL EXPORT PRESERVED
  ========================================================= */

  const handleExcelDownload = () => {
    const payload = {
      finyrname:
        finYear ||
        "2022-2023",

      branch:
        branchId ||
        storedBranchId ||
        "8",

      supplier:
        supplier ||
        "%",

      type:
        projectCode ||
        "%",
    };

    axios
      .get(
        "https://nissi-india.com/api/finPendingTrans_excel",
        {
          params:
            payload,

          headers:
            commonHeaders,

          responseType:
            "blob",
        }
      )
      .then((res) => {
        const url =
          window.URL.createObjectURL(
            new Blob([
              res.data,
            ])
          );

        const link =
          document.createElement(
            "a"
          );

        link.href =
          url;

        link.setAttribute(
          "download",
          "FinYearPendingTransactions.xlsx"
        );

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );

        window.URL.revokeObjectURL(
          url
        );

        toast.success(
          "Excel file downloaded successfully"
        );
      })
      .catch((err) => {
        console.error(
          "Excel download failed",
          err
        );

        toast.error(
          "Failed to download Excel"
        );
      });
  };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = () => {
    const payload = {
      finyrname:
        finYear ||
        "2022-2023",

      branch:
        branchId ||
        storedBranchId ||
        "8",

      supplier:
        supplier ||
        "%",

      type:
        projectCode ||
        "%",
    };

    const query =
      new URLSearchParams(
        payload
      ).toString();

    window.open(
      `/inventory/FinYearPendingReport_Print?${query}`,
      "_blank"
    );
  };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters = () => {
    setBranchId(
      storedBranchId ||
        "8"
    );

    setSupplier("");

    setProjectCode("");

    setData([]);
  };

  /* =========================================================
     TOTAL AMOUNT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const totalAmount =
    useMemo(
      () =>
        data.reduce(
          (
            sum,
            item
          ) =>
            sum +
            (parseFloat(
              item.fld_amount_New ||
                item.amount ||
                item.Amount ||
                item.totalamount ||
                0
            ) ||
              0),
          0
        ),
      [data]
    );

  /* =========================================================
     AVERAGE
  ========================================================= */

  const averageAmount =
    data.length > 0
      ? totalAmount /
        data.length
      : 0;

  /* =========================================================
     NUMBER FORMAT
  ========================================================= */

  const formatNumber = (
    value
  ) => {
    return parseFloat(
      value || 0
    ).toLocaleString(
      "en-IN",
      {
        maximumFractionDigits:
          2,
      }
    );
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Financial Year Pending Transactions
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <CalendarClock className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Financial Year Pending Transactions
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  View and manage pending transactions for the financial year
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT */}

              <button
                type="button"
                onClick={
                  handleExcelDownload
                }
                disabled={
                  data.length ===
                  0
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FaFileExcel
                  size={14}
                />

                Export Excel
              </button>

              {/* FILTER */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER BODY
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* FINANCIAL YEAR */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Financial Year
                  </label>

                  <input
                    type="text"
                    value={
                      finYear ||
                      "2022-2023"
                    }
                    disabled
                    className={
                      disabledInputClass
                    }
                  />
                </div>

                {/* TRANSACTION TYPE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Transaction Type
                  </label>

                  <select
                    value={
                      projectCode
                    }
                    onChange={(e) =>
                      setProjectCode(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Types
                    </option>

                    <option value="P1">
                      Purchase Request
                    </option>

                    <option value="P2">
                      Purchase Order
                    </option>

                    <option value="Q">
                      Quotation
                    </option>

                    <option value="G">
                      GRN
                    </option>

                    <option value="B">
                      Bill Pass
                    </option>
                  </select>
                </div>

                {/* BRANCH */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch
                  </label>

                  <select
                    value={
                      branchId
                    }
                    onChange={(e) =>
                      setBranchId(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    {branchList.map(
                      (
                        b,
                        index
                      ) => (
                        <option
                          key={
                            b.id ||
                            b.fld_Branchid ||
                            b.value ||
                            index
                          }
                          value={
                            b.id ||
                            b.fld_Branchid ||
                            b.value ||
                            ""
                          }
                        >
                          {b.name ||
                            b.fld_BranchName ||
                            b.text ||
                            ""}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* SUPPLIER */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Supplier
                  </label>

                  <select
                    value={
                      supplier
                    }
                    onChange={(e) =>
                      setSupplier(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Suppliers
                    </option>

                    {supplierList.map(
                      (
                        s,
                        index
                      ) => (
                        <option
                          key={
                            s.id ||
                            s.fld_supplierid ||
                            s.value ||
                            index
                          }
                          value={
                            s.id ||
                            s.fld_supplierid ||
                            s.value ||
                            ""
                          }
                        >
                          {s.name ||
                            s.fld_suppliername ||
                            s.text ||
                            ""}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTION ROW
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition ${
                    isDark
                      ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  <FaPrint
                    size={14}
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Pending Transaction Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} transactions
                </span>
              </div>

              {data.length > 0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading pending transactions...
              </p>
            </div>
          ) : data.length > 0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                {/* HEADER */}

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Type
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Branch
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Transaction No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Supplier
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Amount (₹)
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Notes
                    </th>
                  </tr>
                </thead>

                {/* BODY */}

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {data.map(
                    (
                      item,
                      index
                    ) => (
                      <tr
                        key={
                          index
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >

                        {/* S.NO */}

                        <td className="px-4 py-3 font-medium">
                          {index + 1}
                        </td>

                        {/* TYPE */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.fld_transaction ||
                              item.transactionType ||
                              "-"}
                          </span>
                        </td>

                        {/* BRANCH */}

                        <td className="px-4 py-3 font-medium">
                          {item.fld_branchname ||
                            item.branchName ||
                            "-"}
                        </td>

                        {/* DATE */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.fld_date ||
                            item.date ||
                            "-"}
                        </td>

                        {/* TRANSACTION NO */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-blue-900/30 text-blue-300"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {item.fld_transactionno ||
                              item.transactionNo ||
                              "-"}
                          </span>
                        </td>

                        {/* SUPPLIER */}

                        <td className="px-4 py-3">
                          {item.fld_suppliername ||
                            item.supplierName ||
                            "-"}
                        </td>

                        {/* AMOUNT */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap text-right font-semibold ${
                            isDark
                              ? "text-green-300"
                              : "text-green-700"
                          }`}
                        >
                          ₹{" "}
                          {formatNumber(
                            item.fld_amount_New ||
                              item.amount ||
                              0
                          )}
                        </td>

                        {/* NOTES */}

                        <td className="px-4 py-3">
                          {item.fld_notes ||
                            item.notes ||
                            "-"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>

                {/* =========================================
                    TOTAL FOOTER
                ========================================= */}

                <tfoot
                  className={
                    isDark
                      ? "bg-slate-900/70"
                      : "bg-slate-100"
                  }
                >
                  <tr>

                    <td
                      colSpan="6"
                      className={`px-4 py-3 text-right text-sm font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      Total
                    </td>

                    <td
                      className={`px-4 py-3 whitespace-nowrap text-right text-sm font-bold ${
                        isDark
                          ? "text-green-300"
                          : "text-green-700"
                      }`}
                    >
                      ₹{" "}
                      {formatNumber(
                        totalAmount
                      )}
                    </td>

                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaBox
                size={42}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No pending transactions
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>

        {/* ===================================================
            SUMMARY
        =================================================== */}

        {data.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >

            {/* SUMMARY HEADER */}

            <div className="p-6 border-b border-gray-200 dark:border-gray-700">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <div>

                  <h3 className="text-xl font-bold">
                    Summary
                  </h3>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Financial year pending transaction overview
                  </p>
                </div>
              </div>
            </div>

            {/* SUMMARY CONTENT */}

            <div className="p-6">

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

                {/* TOTAL TRANSACTIONS */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Total Transactions
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold text-${baseColor}-${baseShade}`}
                  >
                    {data.length}
                  </p>
                </div>

                {/* TOTAL AMOUNT */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Total Amount
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold ${
                      isDark
                        ? "text-green-300"
                        : "text-green-700"
                    }`}
                  >
                    ₹{" "}
                    {formatNumber(
                      totalAmount
                    )}
                  </p>
                </div>

                {/* AVERAGE */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Average per Transaction
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold ${
                      isDark
                        ? "text-blue-300"
                        : "text-blue-700"
                    }`}
                  >
                    ₹{" "}
                    {formatNumber(
                      averageAmount
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default FinYearPendingTransaction;